/* eslint-disable react/prop-types */
import { Popover } from '@material-ui/core'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import React from 'react'

import * as styles from './styles'

dayjs.extend(relativeTime)

/**
 * Family card on the Parent Dashboard.
 *
 * Compact vertical list of everyone in the family:
 *   - Guardians (Mom, Dad, Nanny, …) with relationship.
 *   - Children with the standard they're in.
 *
 * Each guardian gets a small app-status dot (green = has app installed,
 * gray = no app yet). Click the dot to see device details (app version,
 * last online, device type). Data comes from DeviceDetails rows linked
 * to the guardian via ``actor_guardian`` (M5).
 *
 * Sized to match MembershipBlock (264 × 226) so the dashboard row stays
 * even-height. Phone numbers are intentionally omitted — manage those
 * in the profile modal's Guardians panel.
 */
const AppStatusDot = ({ guardian }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const app = guardian?.app_status || {}
  const hasApp = !!app.has_app

  const openInfo = (e) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }
  const close = () => setAnchorEl(null)

  return (
    <>
      <styles.AppDot
        $hasApp={hasApp}
        onClick={openInfo}
        title={hasApp ? 'Has app — click for details' : 'No app — click for details'}
        aria-label={`app status for ${guardian.first_name || 'guardian'}`}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <styles.PopoverBody>
          <styles.PopoverTitle>App status</styles.PopoverTitle>
          {hasApp ? (
            <>
              <styles.PopoverRow>
                <span>installed</span><span>yes</span>
              </styles.PopoverRow>
              <styles.PopoverRow>
                <span>version</span><span>{app.app_version || '—'}</span>
              </styles.PopoverRow>
              <styles.PopoverRow>
                <span>device</span><span>{app.device_type || '—'}</span>
              </styles.PopoverRow>
              <styles.PopoverRow>
                <span>last online</span>
                <span>{app.last_online ? dayjs(app.last_online).fromNow() : '—'}</span>
              </styles.PopoverRow>
            </>
          ) : (
            <styles.PopoverRow>
              <span>installed</span><span>no</span>
            </styles.PopoverRow>
          )}
        </styles.PopoverBody>
      </Popover>
    </>
  )
}

export const FamilyBlock = ({ selectedParentDetails, onChildClick }) => {
  const guardians = selectedParentDetails?.guardians || []
  const children = selectedParentDetails?.students || []

  const hasAny = guardians.length > 0 || children.length > 0

  return (
    <styles.Wrapper width={264}>
      <styles.CardTitle>Family</styles.CardTitle>

      {!hasAny ? (
        <styles.Empty>No guardians or children on file.</styles.Empty>
      ) : null}

      {guardians.length > 0 ? (
        <>
          <styles.SectionLabel>Guardians</styles.SectionLabel>
          {guardians.map((g) => {
            const fullName = `${g.first_name || ''} ${g.last_name || ''}`.trim() || '(unnamed)'
            return (
              <styles.Row key={g.uuid || fullName}>
                <styles.Name title={fullName}>
                  {g.is_primary_outbound ? <styles.Star>★</styles.Star> : null}
                  {fullName}
                  <AppStatusDot guardian={g} />
                </styles.Name>
                <styles.Role>
                  {g.relationship_display || g.relationship || '—'}
                </styles.Role>
              </styles.Row>
            )
          })}
        </>
      ) : null}

      {children.length > 0 ? (
        <>
          <styles.SectionLabel>Children</styles.SectionLabel>
          {children.map((s) => {
            const studentUuid = s?.user_id || s?.user?.uuid
            const fullName = s?.user?.full_name
              || `${s?.user?.first_name || ''} ${s?.user?.last_name || ''}`.trim()
              || '(unnamed)'
            const standardName = s?.standard?.name
            return (
              <styles.ChildRow
                key={studentUuid || fullName}
                as="button"
                type="button"
                onClick={() => onChildClick && onChildClick(s)}
                title={fullName}
                style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left' }}
              >
                <styles.Name>{fullName}</styles.Name>
                <styles.Role>{standardName || '—'}</styles.Role>
              </styles.ChildRow>
            )
          })}
        </>
      ) : null}
    </styles.Wrapper>
  )
}
